/**
 * SketchTool — interactive 2D sketching on a plane.
 *
 * UX model (SketchUp-flavored):
 *  - Enter sketch mode with an entity tool armed (rect by default). The FIRST
 *    press picks the sketch plane: a Z-facing body face under the cursor, or
 *    the ground. After that all strokes land on the locked plane.
 *  - rect / circle: press-drag-release. line: click-click chaining (click the
 *    first point again to close the loop); double-click or Enter ends open.
 *    arc: three clicks — center, start (radius), end.
 *  - Smart snapping: existing endpoints/centers (strong), then H/V axis
 *    alignment for lines, then a 1mm grid. Typing during a gesture opens the
 *    dimension box; Enter commits exact sizes.
 *  - Enter/✓ commits the sketch as ONE timeline feature (or updates the
 *    feature being edited); Esc cancels the gesture, then exits.
 *
 * Entities accumulate locally and render as an overlay preview — the
 * timeline only sees the finished sketch, so undo stays coarse-grained.
 */

import * as THREE from 'three/webgpu';

import type { CadDocument } from '@core/document';
import {
  arcFromTwoPoints,
  planeBasis,
  planeToWorldMatrix,
  sampleSpline,
  uvToWorld,
} from '@core/geometry/sketch';
import {
  collectPlanarSnaps,
  resolveSnap,
  type SnapKind,
  type SnapPoint,
  type SnapSegment,
} from '@core/geometry/snap';
import { solveSketch } from '@core/geometry/constraints';
import { filletCorner, offsetEntity, polygonEntities, slotEntities, trimEntity } from '@core/geometry/sketchOps';
import { evalParamTable, evaluateExpr, resolveExprs } from '@core/expr';
import { expandTextEntities } from '@core/text/fontCache';
import {
  ref,
  type FeatureId,
  type SketchConstraint,
  type SketchEntity,
  type SketchPlane,
  type SketchPointRef,
  type Vec2,
} from '@core/timeline/types';
import type { DragHandler } from '@render/cadControls';
import type { Viewport } from '@render/viewport';
import { ensureNormals } from '@render/meshView';
import type { DimInput } from '@ui/dimInput';

export type EntityKind =
  | 'line' | 'rect' | 'circle' | 'ellipse' | 'arc' | 'arc2' | 'spline' | 'text'
  | 'polygon' | 'slot';

import { SNAP_COLORS, SNAP_LABELS } from '@ui/snapTheme';

const GRID_SNAP = 1; // mm
const SNAP_PX = 9; // screen-space snap radius for points

const PREVIEW_MATERIAL = new THREE.LineBasicMaterial({ color: 0x6db1f0 });
const RUBBER_MATERIAL = new THREE.LineBasicMaterial({ color: 0xe8c66a });
const PLANE_MATERIAL = new THREE.MeshBasicMaterial({
  color: 0x4c8dd6,
  transparent: true,
  opacity: 0.05,
  side: THREE.DoubleSide,
  depthWrite: false,
});

interface Gesture {
  kind: EntityKind;
  points: Vec2[]; // committed clicks of the in-progress gesture
  hover: Vec2 | null;
}

export class SketchTool {
  active = false;
  entityKind: EntityKind = 'rect';
  /** Set while editing an existing sketch feature. */
  private editingId: FeatureId | null = null;
  private plane: SketchPlane | null = null;
  private matrix: Float32Array | null = null;
  private entities: SketchEntity[] = [];
  /** Constraints being built/edited; committed with the sketch. */
  private constraints: SketchConstraint[] = [];
  /** Sketch-LOCAL undo/redo: snapshots of {entities, constraints}. ⌘Z
   * inside a sketch steps through drawing actions — it must never fall
   * through to document undo (which would rip features out from under the
   * open sketch). */
  private undoStack: { entities: SketchEntity[]; constraints: SketchConstraint[] }[] = [];
  private redoStack: { entities: SketchEntity[]; constraints: SketchConstraint[] }[] = [];
  /** Entity marked (right-click) as the first of a pair constraint. */
  markedEntity: number | null = null;
  /** Trim mode: clicks remove the entity span under the cursor. */
  trimMode = false;
  /** Polygon tool side count — sticky across uses; dim box "R,N" sets it. */
  polygonSides = 6;
  private gesture: Gesture | null = null;
  private hoverClient: { x: number; y: number } | null = null;
  /** Model-derived snap candidates (in-plane body edges), rebuilt on eval. */
  private modelSnapPoints: SnapPoint[] = [];
  private modelSnapSegments: SnapSegment[] = [];
  /** The snap that produced the current hover point — drives the marker. */
  private activeSnap: { p: Vec2; kind: SnapKind; ghost?: { a: Vec2; b: Vec2 } } | null = null;
  /** While no plane is locked, the plane currently previewed under the cursor. */
  private hoverPlaneKey: string | null = null;
  private hoverPlane: SketchPlane | null = null;
  private snapBadge: HTMLDivElement;
  /** Notifies the app shell that hint text / toolbar state changed. */
  onStateChange: (() => void) | null = null;

  /** HTML layer for constraint dimension badges (clickable). */
  private readonly dimLayer: HTMLDivElement;

  constructor(
    private readonly viewport: Viewport,
    /** Live document access — the host may swap projects under us. */
    private readonly host: { readonly doc: CadDocument },
    private readonly dim: DimInput,
  ) {
    viewport.element.addEventListener('pointermove', this.onHover);
    this.snapBadge = document.createElement('div');
    this.snapBadge.className = 'nv-snap-badge';
    this.snapBadge.style.display = 'none';
    viewport.element.appendChild(this.snapBadge);
    this.dimLayer = document.createElement('div');
    this.dimLayer.className = 'nv-sketch-dims';
    viewport.element.appendChild(this.dimLayer);
    // Camera moves reposition badges (drag pans come through pointermove;
    // zoom through wheel).
    viewport.element.addEventListener('wheel', () => this.updateDimBadges(), { passive: true });
  }

  /** Rebuild model snap candidates for the locked plane (called on eval). */
  refreshModelSnaps(): void {
    this.modelSnapPoints = [];
    this.modelSnapSegments = [];
    if (!this.active || !this.matrix) return;
    const report = this.host.doc.lastReport;
    if (!report) return;
    for (const [id, result] of report.results) {
      if (result.kind !== 'body' || report.consumed.has(id)) continue;
      if (result.mesh.edgePositions.length === 0) continue;
      const { points, segments } = collectPlanarSnaps(
        result.mesh.edgePositions,
        this.matrix,
        0.5,
        result.mesh.vertexPositions,
      );
      this.modelSnapPoints.push(...points);
      this.modelSnapSegments.push(...segments);
    }
    this.modelSnapPoints.push({ p: [0, 0], kind: 'origin' });
  }

  // --- lifecycle -------------------------------------------------------------

  start(): void {
    this.reset();
    this.active = true;
    this.onStateChange?.();
  }

  startEditing(id: FeatureId): boolean {
    const feature = this.host.doc.timeline.get(id);
    if (!feature || feature.type !== 'sketch') return false;
    this.reset();
    this.active = true;
    this.editingId = id;
    const params = feature.params as { plane: SketchPlane; entities: readonly SketchEntity[] };
    this.plane = params.plane;
    // Face-attached planes resolve through evaluation — take the matrix from
    // the sketch's own last result; datum planes compute directly.
    const result = this.host.doc.lastReport?.results.get(id);
    if (result?.kind === 'sketch') {
      this.matrix = result.planeWorld;
    } else if (params.plane.kind !== 'face' && params.plane.kind !== 'bface') {
      this.matrix = planeToWorldMatrix(planeBasis(params.plane));
    } else {
      return false; // face plane with no evaluation to resolve against
    }
    this.entities = [...params.entities];
    this.constraints = [...((feature.params as { constraints?: SketchConstraint[] }).constraints ?? [])];
    this.refreshModelSnaps();
    this.renderPreview();
    this.onStateChange?.();
    return true;
  }

  /** Commit as a new feature or update the edited one. True if committed. */
  async finish(): Promise<boolean> {
    if (!this.active) return false;
    const plane = this.plane;
    const entities = this.entities;
    const constraints = this.constraints;
    const editing = this.editingId;
    const hadContent = plane !== null && entities.length > 0;
    this.reset();
    this.onStateChange?.();
    if (!hadContent) return false;
    const params = {
      plane: plane!,
      entities,
      ...(constraints.length > 0 ? { constraints } : {}),
    };
    if (editing) {
      await this.host.doc.apply(`Edit ${this.host.doc.timeline.get(editing)?.name ?? 'Sketch'}`, (t) =>
        t.updateFeature(editing, params),
      );
    } else {
      const n = this.host.doc.timeline.features.filter((f) => f.type === 'sketch').length + 1;
      await this.host.doc.apply(`Create Sketch ${n}`, (t) =>
        t.addFeature('sketch', `Sketch ${n}`, params).timeline,
      );
    }
    return true;
  }

  cancel(): void {
    this.reset();
    this.onStateChange?.();
  }

  /** Esc pressed: cancel gesture first, then exit (committing drawn work). */
  handleEscape(): 'gesture-cancelled' | 'finished' | 'inactive' {
    if (!this.active) return 'inactive';
    if (this.gesture) {
      this.gesture = null;
      this.dim.hide();
      this.renderPreview();
      return 'gesture-cancelled';
    }
    void this.finish();
    return 'finished';
  }

  get hint(): string {
    if (!this.active) return '';
    if (!this.plane) return 'Hover a face to preview its plane and snap points — the first stroke locks it';
    if (this.gesture?.kind === 'line') return 'Click to chain lines · click the start point to close · Enter/double-click to end';
    if (this.gesture?.kind === 'spline') return 'Click through-points · click the start point to close · Enter/double-click ends open';
    if (this.gesture?.kind === 'arc') return this.gesture.points.length === 1 ? 'Click the arc start point' : 'Click the arc end point';
    if (this.gesture?.kind === 'arc2') return this.gesture.points.length === 1 ? 'Click the arc end point' : 'Click to set the bulge · type an exact bulge';
    if (this.trimMode) return 'Trim: click the piece of an entity to remove (cuts where others cross) · X exits';
    if (this.gesture?.kind === 'polygon') return `Drag to a vertex (${this.polygonSides} sides) · type "radius,sides"`;
    if (this.gesture?.kind === 'slot') return this.gesture.points.length === 1 ? 'Click the centerline end' : 'Click to set the width · type an exact width';
    return 'Draw · type for exact size · right-click an entity to constrain it (Offset there too) · Enter to finish';
  }

  private reset(): void {
    this.active = false;
    this.editingId = null;
    this.plane = null;
    this.matrix = null;
    this.entities = [];
    this.constraints = [];
    this.undoStack = [];
    this.redoStack = [];
    this.markedEntity = null;
    this.trimMode = false;
    this.gesture = null;
    this.modelSnapPoints = [];
    this.modelSnapSegments = [];
    this.activeSnap = null;
    this.hoverPlaneKey = null;
    this.hoverPlane = null;
    this.snapBadge.style.display = 'none';
    this.dimLayer.innerHTML = '';
    this.dim.hide();
    this.viewport.overlay.clear();
    this.viewport.requestRender();
  }

  setEntityKind(kind: EntityKind): void {
    this.entityKind = kind;
    this.trimMode = false;
    this.gesture = null;
    this.dim.hide();
    this.renderPreview();
    this.onStateChange?.();
  }

  /** Toggle trim mode: clicks delete the entity SPAN under the cursor. */
  toggleTrim(): void {
    this.trimMode = !this.trimMode;
    this.gesture = null;
    this.dim.hide();
    this.renderPreview();
    this.onStateChange?.();
  }

  /** Trim the clicked span of the entity nearest the click. */
  private trimAt(e: PointerEvent): void {
    const uv = this.project(e);
    if (!uv) return;
    const idx = this.entityAt(e.clientX, e.clientY);
    if (idx === null) return;
    const replacement = trimEntity(this.entities, idx, uv);
    if (replacement === null) return; // spline/text — not trimmable
    this.snapshot();
    // Splice in the replacement; constraints on the old entity go, others reindex.
    this.entities.splice(idx, 1, ...replacement);
    const shift = replacement.length - 1;
    const remap = (en: number): number => (en > idx ? en + shift : en);
    this.constraints = this.constraints
      .filter((c) => !constraintEntities(c).includes(idx))
      .map((c) => remapConstraint(c, remap));
    this.markedEntity = null;
    this.renderPreview();
    this.onStateChange?.();
  }

  /**
   * Fillet the corner nearest `uv` between line `idx` and whichever OTHER
   * line shares that endpoint. Returns false when nothing filletable.
   */
  filletCornerAt(idx: number, uv: Vec2, radius: number): boolean {
    const line = this.entities[idx];
    if (!line || line.kind !== 'line') return false;
    // The line's endpoint nearest the click is the corner candidate.
    const corner = dist(line.a, uv) <= dist(line.b, uv) ? line.a : line.b;
    for (const [j, other] of this.entities.entries()) {
      if (j === idx || other.kind !== 'line') continue;
      if (dist(other.a, corner) > 1e-6 && dist(other.b, corner) > 1e-6) continue;
      const result = filletCorner(line, other, radius);
      if (!result) continue;
      this.snapshot();
      this.entities[idx] = result.a;
      this.entities[j] = result.b;
      this.entities.push(result.arc);
      // filletCorner re-orients each line as {a: farEnd, b: tangentPoint},
      // so point-referencing constraints on them would silently rebind to
      // the wrong/moved points — drop them, like trim does.
      this.constraints = this.constraints.filter((c) => {
        const refs = constraintEntities(c);
        return !refs.includes(idx) && !refs.includes(j);
      });
      this.resolveAndSolve();
      this.renderPreview();
      this.onStateChange?.();
      return true;
    }
    return false;
  }

  /** Offset an entity (context menu): parallel line / concentric circle. */
  offsetEntityAt(idx: number, distance: number, side?: Vec2): boolean {
    const ent = this.entities[idx];
    if (!ent) return false;
    const off = offsetEntity(ent, distance, side);
    if (!off) return false;
    this.snapshot();
    this.entities.push(off);
    this.renderPreview();
    this.onStateChange?.();
    return true;
  }

  // --- input -----------------------------------------------------------------

  /** CADControls.onDragStart hook — claims presses while sketching. */
  tryStart = (e: PointerEvent): DragHandler | null => {
    if (!this.active) return null;
    if (e.altKey || e.button !== 0) return null; // Alt+left orbits, like every other mode
    // Editing: pressing ON a control point of an existing entity and
    // DRAGGING moves it (live constraint re-solve). A plain click falls
    // through to drawing, so starting a line at an endpoint still works.
    if (this.plane && !this.gesture) {
      const raw = this.project(e);
      const hit = raw ? this.hitControlPoint(raw) : null;
      if (hit) {
        let dragging = false;
        const sx = e.clientX;
        const sy = e.clientY;
        return {
          move: (ev: PointerEvent): void => {
            if (!dragging && Math.hypot(ev.clientX - sx, ev.clientY - sy) > 4) {
              dragging = true;
              this.snapshot(); // one undo step per point drag
            }
            if (dragging) this.dragControlPoint(hit, ev);
          },
          up: (ev: PointerEvent): void => {
            if (dragging) {
              this.onStateChange?.();
              return;
            }
            this.beginDraw(ev); // plain click → normal drawing flow
          },
        };
      }
    }
    return this.beginDraw(e);
  };

  private beginDraw(e: PointerEvent): DragHandler | null {
    if (!this.active) return null;
    if (!this.plane) {
      // Honor the hover preview: if the previewed plane still yields a real
      // snap at the click point (corner clicks miss the body raycast!),
      // commit IT — the user is clicking what the marker showed them.
      let committed = false;
      if (this.hoverPlane && this.matrix) {
        const raw = this.project(e);
        if (raw) {
          this.snap(raw, false);
          if (this.activeSnap && !this.activeSnap.kind.startsWith('axis')) {
            this.plane = this.hoverPlane;
            committed = true;
          }
        }
      }
      if (!committed && !this.pickPlane(e)) return null;
      this.refreshModelSnaps(); // plane just locked — index in-plane edges
    }

    if (this.trimMode) {
      this.trimAt(e);
      return null;
    }

    const uv = this.snap(this.project(e), this.axisFor(this.entityKind));
    if (!uv) return null;

    if (!this.gesture) {
      this.gesture = { kind: this.entityKind, points: [uv], hover: uv };
    } else {
      this.gesture.points.push(uv);
    }
    this.advanceGesture(e, false);

    return {
      move: (ev: PointerEvent): void => {
        if (!this.gesture) return;
        const p = this.snap(this.project(ev), this.axisFor(this.gesture.kind));
        if (p) {
          this.gesture.hover = p;
          this.hoverClient = { x: ev.clientX, y: ev.clientY };
          this.renderPreview();
          this.showDims(ev);
        }
      },
      up: (ev: PointerEvent): void => {
        if (!this.gesture) return;
        const start = this.gesture.points[this.gesture.points.length - 1]!;
        const p = this.snap(this.project(ev), this.axisFor(this.gesture.kind)) ?? start;
        const dragged = dist(start, p) > 1e-6;
        if (dragged) this.gesture.points.push(p);
        this.advanceGesture(ev, true);
      },
    };
  }

  /** Rubber-band tracking between clicks + snap feedback while hovering. */
  private onHover = (e: PointerEvent): void => {
    if (!this.active) return;
    if (e.buttons !== 0) return; // pressed moves come through the drag handler
    if (
      e.target instanceof Element &&
      e.target.closest('.nv-side, .nv-props, .nv-toolbar, .nv-hint, .nv-dim, .nv-menu')
    ) {
      return; // hovering UI chrome, not the model
    }

    // No plane locked yet: PREVIEW the plane under the cursor (tint + snap
    // candidates), so the first click can aim at a live snap point. STICKY:
    // at silhouette corners/edges the ray slips off the body and would flip
    // the preview to whatever is behind — keep the current plane as long as
    // the cursor still produces a real snap on it.
    if (!this.plane) {
      let keep = false;
      if (this.matrix && this.hoverPlaneKey) {
        const raw = this.project(e);
        if (raw) {
          this.snap(raw, false);
          keep = this.activeSnap !== null && !this.activeSnap.kind.startsWith('axis');
        }
      }
      if (!keep) {
        const resolved = this.resolvePlaneAt(e);
        const key = resolved ? JSON.stringify(resolved.plane) : null;
        if (key !== this.hoverPlaneKey) {
          this.hoverPlaneKey = key;
          this.hoverPlane = resolved?.plane ?? null;
          this.matrix = resolved?.matrix ?? null;
          this.refreshModelSnaps(); // rebuild candidates for the previewed plane
        }
      }
      if (!this.matrix) {
        this.activeSnap = null;
        this.renderPreview();
        return;
      }
    }
    if (!this.matrix) return;

    // Before the first click there's no gesture, so fall back to the armed
    // tool — the axis guide should preview under the cursor before you draw.
    const p = this.snap(this.project(e), this.axisFor(this.gesture?.kind ?? this.entityKind));
    if (!p) return;
    this.hoverClient = { x: e.clientX, y: e.clientY };
    if (this.gesture) {
      this.gesture.hover = p;
      this.renderPreview();
      this.showDims(e);
    } else {
      // No gesture yet — still show what the first click would snap to.
      this.renderPreview();
    }
  };

  /** Digit typed while a gesture is live → dimension box takes over. */
  handleTypedChar(char: string): boolean {
    if (!this.active || !this.gesture) return false;
    this.dim.beginEdit(char);
    return this.dim.isEditing;
  }

  /** Enter pressed: end a line chain / open spline, else finish the sketch. */
  handleEnter(): boolean {
    if (!this.active) return false;
    if (this.gesture?.kind === 'line') {
      this.endLineChain();
      return true;
    }
    if (this.gesture?.kind === 'spline') {
      this.commitSpline(false);
      return true;
    }
    void this.finish();
    return true;
  }

  handleDoubleClick(): boolean {
    if (this.active && this.gesture?.kind === 'line') {
      this.endLineChain();
      return true;
    }
    if (this.active && this.gesture?.kind === 'spline') {
      this.commitSpline(false);
      return true;
    }
    return this.active;
  }

  // --- gesture state machine ---------------------------------------------------

  /** Called after each press/release; commits entities when a gesture completes. */
  private advanceGesture(e: PointerEvent, isRelease: boolean): void {
    const g = this.gesture;
    if (!g) return;

    if (g.kind === 'text') {
      // Single click places the anchor; the dim box takes the string.
      const at = g.points[0]!;
      this.gesture = null;
      this.dim.show('text', e.clientX, e.clientY, (_values, raw) => {
        const text = raw.trim();
        if (!text) return;
        this.snapshot();
        this.entities.push({ kind: 'text', at, height: 10, text });
        this.renderPreview();
        this.onStateChange?.();
      });
      this.dim.acceptText = true;
      this.dim.beginEdit('');
      return;
    }
    if (g.kind === 'rect' || g.kind === 'circle') {
      if (g.points.length >= 2 && isRelease) {
        const [a, b] = [g.points[0]!, g.points[g.points.length - 1]!];
        if (dist(a, b) > 1e-6) this.commitRectOrCircle(g.kind, a, b);
        this.gesture = null;
        this.dim.hide();
      }
    } else if (g.kind === 'ellipse') {
      // Press centre, release at a "corner": rx = |Δu|, ry = |Δv|.
      if (g.points.length >= 2 && isRelease) {
        const [a, b] = [g.points[0]!, g.points[g.points.length - 1]!];
        const rx = Math.abs(b[0] - a[0]);
        const ry = Math.abs(b[1] - a[1]);
        if (rx > 1e-6 && ry > 1e-6) {
          this.snapshot();
          this.entities.push({ kind: 'ellipse', center: a, rx, ry });
        }
        this.gesture = null;
        this.dim.hide();
      }
    } else if (g.kind === 'polygon') {
      // Press center, release at a vertex (dim box: "radius,sides").
      if (g.points.length >= 2 && isRelease) {
        const [a, b] = [g.points[0]!, g.points[g.points.length - 1]!];
        if (dist(a, b) > 1e-6) this.commitPolygon(a, dist(a, b), this.polygonSides, b);
        this.gesture = null;
        this.dim.hide();
      }
    } else if (g.kind === 'slot') {
      // Three clicks: centerline start, end, then a point setting the width.
      if (g.points.length >= 3) {
        const [a, b, w] = [g.points[0]!, g.points[1]!, g.points[g.points.length - 1]!];
        this.commitSlot(a, b, 2 * distToSeg(w, a, b));
        this.gesture = null;
        this.dim.hide();
      }
    } else if (g.kind === 'line') {
      // Each click adds a vertex; segments commit as we go.
      if (g.points.length >= 2) {
        const a = g.points[g.points.length - 2]!;
        const b = g.points[g.points.length - 1]!;
        if (dist(a, b) > 1e-6) {
          this.snapshot();
          this.entities.push({ kind: 'line', a, b });
          // Closing the loop (back to the chain's first point) ends the chain.
          if (dist(b, g.points[0]!) < 1e-6) {
            this.gesture = null;
            this.dim.hide();
          } else {
            g.points = [b];
          }
        } else if (!isRelease) {
          g.points.pop(); // zero-length click — ignore
        } else {
          g.points = [g.points[g.points.length - 1]!];
        }
      }
    } else if (g.kind === 'spline') {
      // Clicks accumulate through-points; clicking the FIRST point (with 3+
      // points down) closes the spline. Enter/double-click ends it open.
      if (
        g.points.length >= 4 &&
        dist(g.points[g.points.length - 1]!, g.points[0]!) < 1e-6
      ) {
        g.points.pop();
        this.commitSpline(true);
      } else if (
        g.points.length >= 2 &&
        dist(g.points[g.points.length - 1]!, g.points[g.points.length - 2]!) < 1e-6
      ) {
        g.points.pop(); // zero-length click — ignore
      }
    } else if (g.kind === 'arc') {
      if (g.points.length >= 3) {
        this.commitArc(g.points[0]!, g.points[1]!, g.points[g.points.length - 1]!);
        this.gesture = null;
        this.dim.hide();
      }
    } else if (g.kind === 'arc2') {
      if (g.points.length >= 3) {
        const arc = arcFromTwoPoints(
          g.points[0]!,
          g.points[1]!,
          bulgeOf(g.points[0]!, g.points[1]!, g.points[g.points.length - 1]!),
        );
        if (arc) {
          this.snapshot();
          this.entities.push(arc);
        }
        this.gesture = null;
        this.dim.hide();
      }
    }
    this.renderPreview();
    this.showDims(e);
    this.onStateChange?.();
  }

  private endLineChain(): void {
    this.gesture = null;
    this.dim.hide();
    this.renderPreview();
  }

  /** Commit the in-progress spline gesture (open or closed). */
  private commitSpline(closed: boolean): void {
    const g = this.gesture;
    if (g?.kind !== 'spline') return;
    if (g.points.length >= (closed ? 3 : 2)) {
      this.snapshot();
      this.entities.push({
        kind: 'spline',
        points: [...g.points],
        ...(closed ? { closed: true } : {}),
      });
    }
    this.gesture = null;
    this.dim.hide();
    this.renderPreview();
    this.onStateChange?.();
  }

  private commitRectOrCircle(kind: 'rect' | 'circle', a: Vec2, b: Vec2): void {
    this.snapshot();
    if (kind === 'rect') {
      this.entities.push({
        kind: 'rect',
        min: [Math.min(a[0], b[0]), Math.min(a[1], b[1])],
        max: [Math.max(a[0], b[0]), Math.max(a[1], b[1])],
      });
    } else {
      this.entities.push({ kind: 'circle', center: a, radius: dist(a, b) });
    }
  }

  private commitPolygon(center: Vec2, radius: number, sides: number, toward?: Vec2): void {
    if (radius < 1e-6) return;
    this.snapshot();
    this.polygonSides = Math.max(3, Math.min(64, Math.round(sides)));
    this.entities.push(...polygonEntities(center, radius, this.polygonSides, toward));
  }

  private commitSlot(a: Vec2, b: Vec2, width: number): void {
    const slot = slotEntities(a, b, width);
    if (!slot) return;
    this.snapshot();
    this.entities.push(...slot);
  }

  private commitArc(center: Vec2, start: Vec2, end: Vec2): void {
    const radius = dist(center, start);
    if (radius < 1e-6) return;
    this.snapshot();
    this.entities.push({
      kind: 'arc',
      center,
      radius,
      startAngle: Math.atan2(start[1] - center[1], start[0] - center[0]),
      endAngle: Math.atan2(end[1] - center[1], end[0] - center[0]),
    });
  }

  /** Dimension-box commit: exact values finish the active gesture. */
  private commitExact = (values: number[]): void => {
    const g = this.gesture;
    if (!g || g.points.length === 0) return;
    const a = g.points[0]!;
    const h = g.hover ?? a;
    if (g.kind === 'rect') {
      const w = Math.abs(values[0] ?? 10);
      const ht = Math.abs(values[1] ?? values[0] ?? 10);
      const sx = h[0] >= a[0] ? 1 : -1;
      const sy = h[1] >= a[1] ? 1 : -1;
      this.commitRectOrCircle('rect', a, [a[0] + sx * w, a[1] + sy * ht]);
    } else if (g.kind === 'circle') {
      this.commitRectOrCircle('circle', a, [a[0] + Math.abs(values[0] ?? 5), a[1]]);
    } else if (g.kind === 'line') {
      const len = Math.abs(values[0] ?? 0);
      const d = dist(a, h) || 1;
      const b: Vec2 = [a[0] + ((h[0] - a[0]) / d) * len, a[1] + ((h[1] - a[1]) / d) * len];
      this.snapshot();
      this.entities.push({ kind: 'line', a, b });
      g.points = [b];
      this.renderPreview();
      this.onStateChange?.();
      return;
    } else if (g.kind === 'arc2' && g.points.length >= 2) {
      // Typed value = exact bulge magnitude, on the side the cursor is on.
      const side = Math.sign(bulgeOf(a, g.points[1]!, h)) || 1;
      const arc = arcFromTwoPoints(a, g.points[1]!, side * Math.abs(values[0] ?? 0));
      if (arc) {
        this.snapshot();
        this.entities.push(arc);
      }
    } else if (g.kind === 'ellipse') {
      // "rx,ry" — one value = both radii (a circle-ish ellipse).
      const rx = Math.abs(values[0] ?? 10);
      const ry = Math.abs(values[1] ?? values[0] ?? 10);
      if (rx > 1e-6 && ry > 1e-6) {
        this.snapshot();
        this.entities.push({ kind: 'ellipse', center: a, rx, ry });
      }
    } else if (g.kind === 'polygon') {
      // "radius,sides" — either may be omitted (keeps current).
      const r = Math.abs(values[0] ?? 0) || dist(a, h) || 10;
      const n = values[1] ?? this.polygonSides;
      this.commitPolygon(a, r, n, h);
    } else if (g.kind === 'slot' && g.points.length >= 2) {
      // Typed value = exact overall width.
      this.commitSlot(a, g.points[1]!, Math.abs(values[0] ?? 0) || 10);
    }
    this.gesture = null;
    this.renderPreview();
    this.onStateChange?.();
  };

  // --- sketch-local undo/redo -------------------------------------------------------

  private capture(): { entities: SketchEntity[]; constraints: SketchConstraint[] } {
    return {
      entities: JSON.parse(JSON.stringify(this.entities)) as SketchEntity[],
      constraints: JSON.parse(JSON.stringify(this.constraints)) as SketchConstraint[],
    };
  }

  /** Record the CURRENT state as an undo point (call before mutating). */
  private snapshot(): void {
    this.undoStack.push(this.capture());
    if (this.undoStack.length > 100) this.undoStack.shift();
    this.redoStack = [];
  }

  /** ⌘Z while sketching: cancel an in-flight gesture, else step back. */
  undo(): void {
    if (!this.active) return;
    if (this.gesture) {
      this.gesture = null;
      this.dim.hide();
      this.renderPreview();
      return;
    }
    const prev = this.undoStack.pop();
    if (!prev) return;
    this.redoStack.push(this.capture());
    this.entities = prev.entities;
    this.constraints = prev.constraints;
    this.markedEntity = null;
    this.renderPreview();
    this.onStateChange?.();
  }

  redo(): void {
    if (!this.active) return;
    const next = this.redoStack.pop();
    if (!next) return;
    this.undoStack.push(this.capture());
    this.entities = next.entities;
    this.constraints = next.constraints;
    this.renderPreview();
    this.onStateChange?.();
  }

  // --- editing existing entities ---------------------------------------------------

  /** A draggable control point: which entity + how a new position applies. */
  private hitControlPoint(
    uv: Vec2,
  ): { entity: number; apply: (p: Vec2) => SketchEntity } | null {
    const tol = this.snapTolerance(uv) * 0.9;
    let best: { entity: number; d: number; apply: (p: Vec2) => SketchEntity } | null = null;
    const consider = (
      entity: number,
      at: Vec2,
      apply: (p: Vec2) => SketchEntity,
    ): void => {
      const d = dist(at, uv);
      if (d < tol && (!best || d < best.d)) best = { entity, d, apply };
    };

    this.entities.forEach((ent, i) => {
      switch (ent.kind) {
        case 'line':
          consider(i, ent.a, (p) => ({ ...ent, a: p }));
          consider(i, ent.b, (p) => ({ ...ent, b: p }));
          break;
        case 'rect': {
          const corners: [Vec2, (p: Vec2) => [Vec2, Vec2]][] = [
            [ent.min, (p) => [p, ent.max]],
            [ent.max, (p) => [ent.min, p]],
            [[ent.max[0], ent.min[1]], (p) => [[ent.min[0], p[1]], [p[0], ent.max[1]]]],
            [[ent.min[0], ent.max[1]], (p) => [[p[0], ent.min[1]], [ent.max[0], p[1]]]],
          ];
          for (const [at, calc] of corners) {
            consider(i, at, (p) => {
              const [a, b] = calc(p);
              return {
                kind: 'rect',
                min: [Math.min(a[0], b[0]), Math.min(a[1], b[1])],
                max: [Math.max(a[0], b[0]), Math.max(a[1], b[1])],
              };
            });
          }
          break;
        }
        case 'circle': {
          consider(i, ent.center, (p) => ({ ...ent, center: p }));
          // Rim handle: nearest point on the circumference → radius drag.
          const d = dist(ent.center, uv) || 1;
          const rim: Vec2 = [
            ent.center[0] + ((uv[0] - ent.center[0]) / d) * ent.radius,
            ent.center[1] + ((uv[1] - ent.center[1]) / d) * ent.radius,
          ];
          consider(i, rim, (p) => ({ ...ent, radius: Math.max(0.05, dist(ent.center, p)) }));
          break;
        }
        case 'ellipse': {
          consider(i, ent.center, (p) => ({ ...ent, center: p }));
          // Axis handles at (±rx, 0) and (0, ±ry) drag each radius.
          consider(i, [ent.center[0] + ent.rx, ent.center[1]], (p) => ({
            ...ent, rx: Math.max(0.05, Math.abs(p[0] - ent.center[0])),
          }));
          consider(i, [ent.center[0], ent.center[1] + ent.ry], (p) => ({
            ...ent, ry: Math.max(0.05, Math.abs(p[1] - ent.center[1])),
          }));
          break;
        }
        case 'arc': {
          consider(i, ent.center, (p) => ({ ...ent, center: p }));
          for (const [key, ang] of [['startAngle', ent.startAngle], ['endAngle', ent.endAngle]] as const) {
            const at: Vec2 = [
              ent.center[0] + ent.radius * Math.cos(ang),
              ent.center[1] + ent.radius * Math.sin(ang),
            ];
            consider(i, at, (p) => ({
              ...ent,
              radius: Math.max(0.05, dist(ent.center, p)),
              [key]: Math.atan2(p[1] - ent.center[1], p[0] - ent.center[0]),
            }));
          }
          break;
        }
        case 'spline':
          ent.points.forEach((pt, k) => {
            consider(i, pt, (p) => ({
              ...ent,
              points: ent.points.map((q, m) => (m === k ? p : q)),
            }));
          });
          break;
        case 'text':
          consider(i, ent.at, (p) => ({ ...ent, at: p })); // drag the anchor
          break;
      }
    });
    return best;
  }

  /** Move a control point (snapped), re-solving constraints live. */
  private dragControlPoint(
    hit: { entity: number; apply: (p: Vec2) => SketchEntity },
    ev: PointerEvent,
  ): void {
    const raw = this.project(ev);
    if (!raw) return;
    const p = this.snap(raw, false, hit.entity) ?? raw;
    this.entities[hit.entity] = hit.apply(p);
    if (this.constraints.length > 0) {
      try {
        const scope = evalParamTable(this.host.doc.timeline.params).values;
        const solved = solveSketch(
          this.entities,
          resolveExprs(this.constraints, scope),
        );
        if (solved.converged) this.entities = solved.entities;
      } catch {
        /* unsolvable mid-drag — show the raw move */
      }
    }
    this.hoverClient = { x: ev.clientX, y: ev.clientY };
    this.renderPreview();
  }

  /**
   * Constraint badges over the sketch: dimension values (click → edit via
   * the dim box, expressions welcome) and glyphs for geometric constraints
   * (click → remove). Rebuilt on every preview render + camera move.
   */
  private updateDimBadges(): void {
    this.dimLayer.innerHTML = '';
    if (!this.active || !this.matrix || this.constraints.length === 0) return;
    const rect = this.viewport.element.getBoundingClientRect();
    const cam = this.viewport.camera;
    const toScreen = (uv: Vec2): [number, number] | null => {
      const [x, y, z] = uvToWorld(this.matrix!, uv);
      const v = new THREE.Vector3(x, y, z).project(cam);
      if (v.z > 1) return null;
      return [((v.x + 1) / 2) * rect.width, ((1 - v.y) / 2) * rect.height];
    };
    const scope = ((): Record<string, number> => {
      try {
        return evalParamTable(this.host.doc.timeline.params).values;
      } catch {
        return {};
      }
    })();
    const mid = (a: Vec2 | null, b: Vec2 | null): Vec2 | null =>
      a && b ? [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2] : (a ?? b);

    this.constraints.forEach((c, ci) => {
      let at: Vec2 | null = null;
      let text = '';
      let editable = false;
      const fmt = (v: number | { $expr: string }): string =>
        typeof v === 'number'
          ? String(Math.round(v * 100) / 100)
          : `${v.$expr} = ${(() => {
              try {
                return Math.round(evaluateExpr(v.$expr, scope) * 100) / 100;
              } catch {
                return '⚠';
              }
            })()}`;
      switch (c.kind) {
        case 'distance':
          at = mid(this.pointPosition(c.a), this.pointPosition(c.b));
          text = fmt(c.value);
          editable = true;
          break;
        case 'radius': {
          const ent = this.entities[c.entity];
          if (ent && (ent.kind === 'circle' || ent.kind === 'arc')) {
            at = [ent.center[0] + ent.radius * 0.7071, ent.center[1] + ent.radius * 0.7071];
          }
          text = `R ${fmt(c.value)}`;
          editable = true;
          break;
        }
        case 'horizontal': text = 'H'; at = this.entityBadgeAnchor(c.entity); break;
        case 'vertical': text = 'V'; at = this.entityBadgeAnchor(c.entity); break;
        case 'parallel': text = '∥'; at = this.entityBadgeAnchor(c.b); break;
        case 'perpendicular': text = '⊥'; at = this.entityBadgeAnchor(c.b); break;
        case 'equal': text = '='; at = this.entityBadgeAnchor(c.b); break;
        case 'tangent': text = '⌒'; at = this.entityBadgeAnchor(c.b); break;
        case 'concentric': text = '⊙'; at = this.entityBadgeAnchor(c.b); break;
        case 'symmetric': text = '⋈'; at = this.entityBadgeAnchor(c.about); break;
        case 'coincident': text = '◉'; at = this.pointPosition(c.a); break;
        case 'fixed': text = '⚓'; at = this.pointPosition(c.point); break;
      }
      if (!at) return;
      const s = toScreen(at);
      if (!s) return;
      const badge = document.createElement('div');
      badge.className = editable ? 'nv-sketch-dim' : 'nv-sketch-glyph';
      badge.textContent = text;
      badge.style.left = `${s[0]}px`;
      badge.style.top = `${s[1]}px`;
      badge.title = editable
        ? 'Click to edit (number or expression)'
        : `${c.kind} — click to remove`;
      badge.addEventListener('pointerdown', (e) => e.stopPropagation());
      badge.addEventListener('click', (e) => {
        e.stopPropagation();
        if (!editable) {
          this.snapshot();
          this.constraints = this.constraints.filter((_x, k) => k !== ci);
          this.resolveAndSolve();
          this.renderPreview();
          this.onStateChange?.();
          return;
        }
        this.dim.show(c.kind, e.clientX, e.clientY, (_values, raw) => {
          const textVal = raw.trim();
          const num = Number(textVal);
          let value: number | { $expr: string };
          if (Number.isFinite(num) && textVal !== '') value = num;
          else {
            try {
              evaluateExpr(textVal, scope);
              value = { $expr: textVal };
            } catch {
              return; // invalid expression — keep the old value
            }
          }
          this.snapshot();
          this.constraints = this.constraints.map((x, k) =>
            k === ci ? ({ ...x, value } as SketchConstraint) : x,
          );
          this.resolveAndSolve();
          this.renderPreview();
          this.onStateChange?.();
        });
        this.dim.acceptText = true;
        this.dim.beginEdit('');
      });
      this.dimLayer.appendChild(badge);
    });
  }

  /** Where a per-entity constraint glyph sits: the entity's midpoint. */
  private entityBadgeAnchor(entity: number): Vec2 | null {
    const ent = this.entities[entity];
    if (!ent) return null;
    const [pts] = entityPolyline(ent);
    if (pts.length === 0) return null;
    const m = pts[Math.floor(pts.length / 2)]!;
    return [m[0], m[1]];
  }

  /** Re-solve current constraints against current entities (best effort). */
  private resolveAndSolve(): void {
    if (this.constraints.length === 0) return;
    try {
      const scope = evalParamTable(this.host.doc.timeline.params).values;
      const solved = solveSketch(this.entities, resolveExprs(this.constraints, scope));
      if (solved.converged) this.entities = solved.entities;
    } catch {
      /* leave entities as-is; evaluation will surface the error */
    }
  }

  /** Delete one entity; constraints referencing it go too, others reindex. */
  deleteEntity(idx: number): void {
    if (!this.entities[idx]) return;
    this.snapshot();
    this.entities.splice(idx, 1);
    const remap = (e: number): number => (e > idx ? e - 1 : e);
    this.constraints = this.constraints
      .filter((c) => !constraintEntities(c).includes(idx))
      .map((c) => remapConstraint(c, remap));
    if (this.markedEntity === idx) this.markedEntity = null;
    else if (this.markedEntity !== null && this.markedEntity > idx) this.markedEntity--;
    this.renderPreview();
    this.onStateChange?.();
  }

  // --- constraints ---------------------------------------------------------------

  /** Entities drawn so far (context-menu constraint building). */
  get currentEntities(): readonly SketchEntity[] {
    return this.entities;
  }

  get constraintCount(): number {
    return this.constraints.length;
  }

  /** Sketch-plane UV under a client point (constraint hit-testing). */
  uvAt(clientX: number, clientY: number): Vec2 | null {
    return this.project({ clientX, clientY } as PointerEvent);
  }

  /** Index of the entity whose outline is nearest the client point. */
  entityAt(clientX: number, clientY: number): number | null {
    const uv = this.uvAt(clientX, clientY);
    if (!uv) return null;
    const tol = this.snapTolerance(uv) * 1.4;
    let best = -1;
    let bestD = tol;
    this.entities.forEach((ent, i) => {
      const [pts, closed] = entityPolyline(ent);
      for (let k = 0; k < pts.length - (closed ? 0 : 1); k++) {
        const a = pts[k]!;
        const b = pts[(k + 1) % pts.length]!;
        const d = pointSegDist(uv, a, b);
        if (d < bestD) {
          bestD = d;
          best = i;
        }
      }
    });
    return best >= 0 ? best : null;
  }

  /** Named point of `entity` nearest the client point (for point constraints). */
  pointRefAt(entity: number, clientX: number, clientY: number): SketchPointRef | null {
    const uv = this.uvAt(clientX, clientY);
    const ent = this.entities[entity];
    if (!uv || !ent) return null;
    const named: [SketchPointRef['point'], Vec2][] = [];
    if (ent.kind === 'line') {
      named.push(['a', ent.a], ['b', ent.b], ['mid', [(ent.a[0] + ent.b[0]) / 2, (ent.a[1] + ent.b[1]) / 2]]);
    } else if (ent.kind === 'rect') {
      named.push(['min', ent.min], ['max', ent.max], ['mid', [(ent.min[0] + ent.max[0]) / 2, (ent.min[1] + ent.max[1]) / 2]]);
    } else if (ent.kind === 'spline') {
      named.push(['a', ent.points[0]!], ['b', ent.points[ent.points.length - 1]!]);
    } else if (ent.kind === 'circle' || ent.kind === 'arc' || ent.kind === 'ellipse') {
      named.push(['center', ent.center]);
    } else {
      return null; // text/poly: fixed geometry, no constrainable points
    }
    let best = named[0]!;
    for (const cand of named) {
      if (dist(cand[1], uv) < dist(best[1], uv)) best = cand;
    }
    return { entity, point: best[0] };
  }

  /** Current position of a named entity point (for 'fixed' constraints). */
  pointPosition(refp: SketchPointRef): Vec2 | null {
    const ent = this.entities[refp.entity];
    if (!ent) return null;
    switch (ent.kind) {
      case 'line':
        return refp.point === 'a' ? ent.a
          : refp.point === 'b' ? ent.b
          : refp.point === 'mid' ? [(ent.a[0] + ent.b[0]) / 2, (ent.a[1] + ent.b[1]) / 2]
          : null;
      case 'rect':
        return refp.point === 'min' ? ent.min
          : refp.point === 'max' ? ent.max
          : refp.point === 'mid' ? [(ent.min[0] + ent.max[0]) / 2, (ent.min[1] + ent.max[1]) / 2]
          : null;
      case 'circle':
      case 'arc':
      case 'ellipse':
        return refp.point === 'center' ? ent.center : null;
      case 'spline':
        return refp.point === 'a' ? ent.points[0]!
          : refp.point === 'b' ? ent.points[ent.points.length - 1]!
          : null;
      case 'text':
      case 'poly':
        return null;
    }
  }

  /**
   * Add a constraint and re-solve the local sketch immediately so the
   * preview snaps into shape. Expression values resolve against `scope`.
   * On failure the constraint is NOT kept; the error string comes back.
   */
  addConstraint(c: SketchConstraint, scope: Readonly<Record<string, number>>): string | null {
    const next = [...this.constraints, c];
    try {
      const resolved = resolveExprs(next, scope) as SketchConstraint[];
      const solved = solveSketch(this.entities, resolved);
      if (!solved.converged) {
        return `Constraint conflicts with existing ones (residual ${solved.maxResidual.toExponential(1)})`;
      }
      this.snapshot();
      this.constraints = next;
      this.entities = solved.entities;
      this.markedEntity = null;
      this.renderPreview();
      this.onStateChange?.();
      return null;
    } catch (err) {
      return err instanceof Error ? err.message : String(err);
    }
  }

  // --- geometry helpers --------------------------------------------------------

  /**
   * Resolve the sketch plane under a pointer position — NO state changes,
   * so hover can preview it before the first click commits. Extrude caps
   * become PARAMETRIC face refs; axis-aligned faces map to datum planes;
   * any other face becomes a free plane; empty space is the ground.
   */
  private resolvePlaneAt(
    e: PointerEvent,
  ): { plane: SketchPlane; matrix: Float32Array } | null {
    const hit = this.viewport.raycastBodies(e.clientX, e.clientY);
    if (!hit) {
      const plane: SketchPlane = { kind: 'datum', plane: 'XY', offset: 0 };
      return { plane, matrix: planeToWorldMatrix(planeBasis(plane)) };
    }

    const result = this.host.doc.lastReport?.results.get(hit.featureId);
    if (result?.kind === 'body' && result.capPlanes) {
      const m = result.capPlanes.top;
      const axis = new THREE.Vector3(m[8]!, m[9]!, m[10]!);
      const align = hit.normal.dot(axis);
      if (Math.abs(align) > 0.99) {
        const which = align > 0 ? 'top' : 'bottom';
        const cap = result.capPlanes[which];
        // The cap must actually CONTAIN the clicked point — a boolean
        // result's caps belong to its tool and can float elsewhere (e.g. a
        // cut's tool-top in mid-air). If it doesn't, fall through to datum.
        const capDist =
          (hit.point.x - cap[12]!) * cap[8]! +
          (hit.point.y - cap[13]!) * cap[9]! +
          (hit.point.z - cap[14]!) * cap[10]!;
        if (Math.abs(capDist) < 0.1) {
          return {
            plane: { kind: 'face', face: ref(hit.featureId, `face:${which}`) },
            matrix: cap,
          };
        }
      }
    }

    // ANY planar B-rep face: parametric — the sketch rides this face
    // (nearest-centroid identity) through regeneration. Works on lofts,
    // filleted bodies, patterned parts… not just extrude caps.
    if (hit.face?.kind === 'planar') {
      const plane: SketchPlane = {
        kind: 'bface',
        body: ref(hit.featureId),
        center: hit.face.center,
        normal: hit.face.dir,
      };
      return {
        plane,
        matrix: planeToWorldMatrix(
          planeBasis({ kind: 'free', origin: hit.face.center, normal: hit.face.dir }),
        ),
      };
    }

    const n = hit.normal;
    const r2 = (v: number): number => Math.round(v * 100) / 100;
    let plane: SketchPlane;
    if (n.z > 0.99) {
      plane = { kind: 'datum', plane: 'XY', offset: r2(hit.point.z) };
    } else if (n.x > 0.99) {
      plane = { kind: 'datum', plane: 'YZ', offset: r2(hit.point.x) };
    } else if (n.y < -0.99) {
      plane = { kind: 'datum', plane: 'XZ', offset: r2(hit.point.y) };
    } else {
      plane = {
        kind: 'free',
        origin: [r2(hit.point.x), r2(hit.point.y), r2(hit.point.z)],
        normal: [n.x, n.y, n.z],
      };
    }
    return { plane, matrix: planeToWorldMatrix(planeBasis(plane)) };
  }

  /** Start sketching with the plane locked at a screen point (context menu). */
  startOnFace(clientX: number, clientY: number): boolean {
    this.start();
    const ok = this.pickPlane({ clientX, clientY } as PointerEvent);
    if (ok) this.refreshModelSnaps();
    else this.cancel();
    this.onStateChange?.();
    return ok;
  }

  /** First stroke: commit the plane under the cursor. */
  private pickPlane(e: PointerEvent): boolean {
    const resolved = this.resolvePlaneAt(e);
    if (!resolved) return false;
    this.plane = resolved.plane;
    this.matrix = resolved.matrix;
    this.renderPreview();
    return true;
  }

  /** Marker + ghost line + label for the current snap (from renderPreview). */
  private renderSnapFeedback(): void {
    const snap = this.activeSnap;
    const client = this.hoverClient;
    if (!snap || !this.matrix || !client) {
      this.snapBadge.style.display = 'none';
      return;
    }
    // Inference ghost line (extension / parallel / perpendicular / axis).
    if (snap.ghost) {
      const color = SNAP_COLORS[snap.kind] ?? 0x9aa3b2;
      const pts = [snap.ghost.a, snap.ghost.b].map(
        (q) => new THREE.Vector3(...uvToWorld(this.matrix!, q)),
      );
      const geo = ensureNormals(new THREE.BufferGeometry().setFromPoints(pts));
      const line = new THREE.Line(
        geo,
        new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.55 }),
      );
      line.renderOrder = 1000;
      line.raycast = () => undefined;
      this.viewport.overlay.add(line);
    }
    const color = SNAP_COLORS[snap.kind];
    if (color !== undefined) {
      const radius = this.snapTolerance(snap.p) * 0.35;
      const marker = new THREE.Mesh(
        new THREE.SphereGeometry(Math.max(radius, 0.4), 10, 10),
        new THREE.MeshBasicMaterial({ color, depthTest: false }),
      );
      marker.position.set(...uvToWorld(this.matrix, snap.p));
      marker.renderOrder = 1001;
      marker.raycast = () => undefined;
      this.viewport.overlay.add(marker);
    }
    const rect = this.viewport.element.getBoundingClientRect();
    this.snapBadge.textContent = SNAP_LABELS[snap.kind] ?? '';
    this.snapBadge.style.display = 'block';
    this.snapBadge.style.left = `${client.x - rect.left + 14}px`;
    this.snapBadge.style.top = `${client.y - rect.top - 26}px`;
  }

  /** Pointer → UV on the sketch plane (null if the ray misses). */
  private project(e: PointerEvent): Vec2 | null {
    if (!this.matrix) return null;
    const rect = this.viewport.element.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1,
    );
    const ray = new THREE.Raycaster();
    ray.setFromCamera(ndc, this.viewport.camera);
    const m = this.matrix;
    const origin = new THREE.Vector3(m[12]!, m[13]!, m[14]!);
    const normal = new THREE.Vector3(m[8]!, m[9]!, m[10]!);
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(normal, origin);
    const world = ray.ray.intersectPlane(plane, new THREE.Vector3());
    if (!world) return null;
    const rel = world.sub(origin);
    const u = new THREE.Vector3(m[0]!, m[1]!, m[2]!);
    const v = new THREE.Vector3(m[4]!, m[5]!, m[6]!);
    return [rel.dot(u), rel.dot(v)];
  }

  /**
   * Snap priority: model/sketch points (endpoints, midpoints, centers,
   * origin) → on-edge → H/V axis (lines) → grid. Records the winning snap
   * for the marker + badge.
   */
  /** Which tools axis-lock their drawing direction (line + 2-point arc chord). */
  private axisFor(kind: EntityKind | undefined): boolean {
    return kind === 'line' || kind === 'arc2';
  }

  private snap(p: Vec2 | null, axisAlign: boolean, excludeEntity?: number): Vec2 | null {
    if (!p) {
      this.activeSnap = null;
      return null;
    }
    const tol = this.snapTolerance(p);

    const points: SnapPoint[] = [...this.modelSnapPoints];
    const segments: SnapSegment[] = [...this.modelSnapSegments];
    /** Sketch-own segments: also the references for parallel/perpendicular. */
    const dirSegments: SnapSegment[] = [];
    const pushSeg = (a: Vec2, b: Vec2): void => {
      segments.push({ a, b });
      dirSegments.push({ a, b });
    };
    for (const [entIdx, ent] of this.entities.entries()) {
      if (entIdx === excludeEntity) continue; // don't self-snap while dragging
      if (ent.kind === 'line') {
        points.push({ p: ent.a, kind: 'endpoint' }, { p: ent.b, kind: 'endpoint' });
        points.push({
          p: [(ent.a[0] + ent.b[0]) / 2, (ent.a[1] + ent.b[1]) / 2],
          kind: 'midpoint',
        });
        pushSeg(ent.a, ent.b);
      } else if (ent.kind === 'rect') {
        const corners: Vec2[] = [
          ent.min,
          [ent.max[0], ent.min[1]],
          ent.max,
          [ent.min[0], ent.max[1]],
        ];
        corners.forEach((c, i) => {
          points.push({ p: c, kind: 'endpoint' });
          const next = corners[(i + 1) % 4]!;
          points.push({ p: [(c[0] + next[0]) / 2, (c[1] + next[1]) / 2], kind: 'midpoint' });
          pushSeg(c, next);
        });
      } else if (ent.kind === 'circle' || ent.kind === 'ellipse') {
        points.push({ p: ent.center, kind: 'center' });
      } else if (ent.kind === 'spline') {
        // Every through-point snaps; the sampled curve gives on-edge.
        for (const p of ent.points) points.push({ p, kind: 'endpoint' });
        const sampled = sampleSpline(ent.points, !!ent.closed, 6);
        for (let i = 0; i < sampled.length - (ent.closed ? 0 : 1); i++) {
          segments.push({ a: sampled[i]!, b: sampled[(i + 1) % sampled.length]! });
        }
      } else if (ent.kind === 'text') {
        points.push({ p: ent.at, kind: 'endpoint' }); // the anchor snaps
      } else if (ent.kind === 'poly') {
        /* glyph outlines: too dense to snap usefully */
      } else {
        points.push({ p: ent.center, kind: 'center' });
        points.push({
          p: [
            ent.center[0] + ent.radius * Math.cos(ent.startAngle),
            ent.center[1] + ent.radius * Math.sin(ent.startAngle),
          ],
          kind: 'endpoint',
        });
        points.push({
          p: [
            ent.center[0] + ent.radius * Math.cos(ent.endAngle),
            ent.center[1] + ent.radius * Math.sin(ent.endAngle),
          ],
          kind: 'endpoint',
        });
      }
    }
    if (this.gesture?.points[0]) points.push({ p: this.gesture.points[0], kind: 'endpoint' });

    const prev =
      this.gesture && this.gesture.points.length > 0
        ? this.gesture.points[this.gesture.points.length - 1]!
        : null;
    const result = resolveSnap(p, tol, points, segments, prev, axisAlign, GRID_SNAP, dirSegments);
    this.activeSnap =
      result.kind === 'grid'
        ? null
        : { p: result.p, kind: result.kind, ghost: result.ghost };
    return result.p;
  }

  /** SNAP_PX converted to sketch-plane mm at the cursor's depth. */
  private snapTolerance(p: Vec2): number {
    if (!this.matrix) return 1;
    const world = new THREE.Vector3(...uvToWorld(this.matrix, p));
    const cam = this.viewport.camera;
    const distToCam = world.distanceTo(cam.position);
    const worldPerPx =
      (2 * distToCam * Math.tan((cam.fov * Math.PI) / 360)) /
      (this.viewport.element.clientHeight || 1);
    return SNAP_PX * worldPerPx;
  }

  // --- preview rendering ---------------------------------------------------------

  private renderPreview(): void {
    const overlay = this.viewport.overlay;
    overlay.clear();
    if (!this.active || !this.matrix) {
      this.viewport.requestRender();
      return;
    }
    const m = this.matrix;

    // Faint plane tint so the user sees the locked sketch surface.
    const planeMesh = new THREE.Mesh(new THREE.PlaneGeometry(500, 500), PLANE_MATERIAL);
    planeMesh.applyMatrix4(new THREE.Matrix4().fromArray(m));
    planeMesh.raycast = () => undefined;
    overlay.add(planeMesh);

    const addPolyline = (pts: Vec2[], closed: boolean, mat: THREE.LineBasicMaterial): void => {
      if (pts.length < 2) return;
      const v3 = pts.map((q) => new THREE.Vector3(...uvToWorld(m, q)));
      if (closed) v3.push(v3[0]!.clone()); // WebGPURenderer has no LineLoop
      const geo = ensureNormals(new THREE.BufferGeometry().setFromPoints(v3));
      overlay.add(new THREE.Line(geo, mat));
    };

    for (const ent of this.entities) {
      if (ent.kind === 'text') {
        // Real glyph outlines when the font is ready; bbox otherwise.
        try {
          for (const e2 of expandTextEntities([ent])) {
            addPolyline(...entityPolyline(e2), PREVIEW_MATERIAL);
          }
          continue;
        } catch {
          /* font loading — bbox fallback below */
        }
      }
      addPolyline(...entityPolyline(ent), PREVIEW_MATERIAL);
    }

    this.renderSnapFeedback();

    const g = this.gesture;
    if (g && g.hover) {
      const a = g.points[0];
      if (g.kind === 'rect' && a) {
        addPolyline(
          [a, [g.hover[0], a[1]], g.hover, [a[0], g.hover[1]]],
          true,
          RUBBER_MATERIAL,
        );
      } else if (g.kind === 'circle' && a) {
        addPolyline(...entityPolyline({ kind: 'circle', center: a, radius: dist(a, g.hover) }), RUBBER_MATERIAL);
      } else if (g.kind === 'ellipse' && a) {
        const rx = Math.abs(g.hover[0] - a[0]);
        const ry = Math.abs(g.hover[1] - a[1]);
        if (rx > 1e-6 && ry > 1e-6) {
          addPolyline(...entityPolyline({ kind: 'ellipse', center: a, rx, ry }), RUBBER_MATERIAL);
        }
      } else if (g.kind === 'line' && g.points.length > 0) {
        addPolyline([g.points[g.points.length - 1]!, g.hover], false, RUBBER_MATERIAL);
      } else if (g.kind === 'spline' && g.points.length > 0) {
        addPolyline(
          sampleSpline([...g.points, g.hover], false),
          false,
          RUBBER_MATERIAL,
        );
      } else if (g.kind === 'arc' && a) {
        if (g.points.length === 1) {
          addPolyline([a, g.hover], false, RUBBER_MATERIAL);
        } else {
          const start = g.points[1]!;
          const r = dist(a, start);
          addPolyline(
            ...entityPolyline({
              kind: 'arc',
              center: a,
              radius: r,
              startAngle: Math.atan2(start[1] - a[1], start[0] - a[0]),
              endAngle: Math.atan2(g.hover[1] - a[1], g.hover[0] - a[0]),
            }),
            RUBBER_MATERIAL,
          );
        }
      } else if (g.kind === 'arc2' && a) {
        if (g.points.length === 1) {
          addPolyline([a, g.hover], false, RUBBER_MATERIAL);
        } else {
          const b = g.points[1]!;
          const arc = arcFromTwoPoints(a, b, bulgeOf(a, b, g.hover));
          if (arc) addPolyline(...entityPolyline(arc), RUBBER_MATERIAL);
          else addPolyline([a, b], false, RUBBER_MATERIAL); // ~zero bulge: chord
        }
      } else if (g.kind === 'polygon' && a) {
        const r = dist(a, g.hover);
        if (r > 1e-6) {
          const pts = polygonEntities(a, r, this.polygonSides, g.hover)
            .map((e) => (e.kind === 'line' ? e.a : [0, 0] as Vec2));
          addPolyline(pts, true, RUBBER_MATERIAL);
        }
      } else if (g.kind === 'slot' && a) {
        if (g.points.length === 1) {
          addPolyline([a, g.hover], false, RUBBER_MATERIAL);
        } else {
          const b = g.points[1]!;
          const w = 2 * distToSeg(g.hover, a, b);
          const slot = w > 1e-6 ? slotEntities(a, b, w) : null;
          if (slot) for (const s of slot) addPolyline(...entityPolyline(s), RUBBER_MATERIAL);
          else addPolyline([a, b], false, RUBBER_MATERIAL);
        }
      }
    }
    this.updateDimBadges();
    this.viewport.requestRender();
  }

  private showDims(e: PointerEvent | { clientX: number; clientY: number }): void {
    const g = this.gesture;
    if (!g || !g.hover || g.points.length === 0) {
      this.dim.hide();
      return;
    }
    const a = g.points[0]!;
    const h = g.hover;
    let text = '';
    if (g.kind === 'rect') text = `${Math.abs(h[0] - a[0]).toFixed(1)} × ${Math.abs(h[1] - a[1]).toFixed(1)}`;
    else if (g.kind === 'circle') text = `r ${dist(a, h).toFixed(1)}`;
    else if (g.kind === 'ellipse') text = `rx ${Math.abs(h[0] - a[0]).toFixed(1)} · ry ${Math.abs(h[1] - a[1]).toFixed(1)}`;
    else if (g.kind === 'line') text = `${dist(g.points[g.points.length - 1]!, h).toFixed(1)}`;
    else if (g.kind === 'arc2' && g.points.length >= 2) {
      const arc = arcFromTwoPoints(a, g.points[1]!, bulgeOf(a, g.points[1]!, h));
      text = arc ? `bulge ${bulgeOf(a, g.points[1]!, h).toFixed(1)} · r ${arc.radius.toFixed(1)}` : 'bulge 0';
    } else if (g.kind === 'arc' && g.points.length >= 2) text = `r ${dist(a, g.points[1]!).toFixed(1)}`;
    else if (g.kind === 'polygon') text = `r ${dist(a, h).toFixed(1)} · ${this.polygonSides} sides (type r,n)`;
    else if (g.kind === 'slot') {
      text = g.points.length >= 2
        ? `w ${(2 * distToSeg(h, a, g.points[1]!)).toFixed(1)}`
        : `L ${dist(a, h).toFixed(1)}`;
    }
    else text = `r ${dist(a, h).toFixed(1)}`;
    this.dim.show(text, e.clientX, e.clientY, this.commitExact);
  }
}

function dist(a: Vec2, b: Vec2): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1]);
}

/** Distance from point P to the INFINITE line through A→B (slot width). */
function distToSeg(p: Vec2, a: Vec2, b: Vec2): number {
  const len = dist(a, b);
  if (len < 1e-9) return dist(p, a);
  return Math.abs(bulgeOf(a, b, p));
}

/** Distance from point P to segment A→B. */
function pointSegDist(p: Vec2, a: Vec2, b: Vec2): number {
  const abx = b[0] - a[0];
  const aby = b[1] - a[1];
  const len2 = abx * abx + aby * aby;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((p[0] - a[0]) * abx + (p[1] - a[1]) * aby) / len2));
  return Math.hypot(p[0] - (a[0] + t * abx), p[1] - (a[1] + t * aby));
}

/** Entity indices a constraint references. */
function constraintEntities(c: SketchConstraint): number[] {
  switch (c.kind) {
    case 'coincident': return [c.a.entity, c.b.entity];
    case 'horizontal':
    case 'vertical': return [c.entity];
    case 'parallel':
    case 'perpendicular':
    case 'equal':
    case 'tangent':
    case 'concentric': return [c.a, c.b];
    case 'distance': return [c.a.entity, c.b.entity];
    case 'radius': return [c.entity];
    case 'fixed': return [c.point.entity];
    case 'symmetric': return [c.a.entity, c.b.entity, c.about];
  }
}

/** Rewrite a constraint's entity indices (after a deletion). */
function remapConstraint(c: SketchConstraint, f: (e: number) => number): SketchConstraint {
  switch (c.kind) {
    case 'coincident':
      return { ...c, a: { ...c.a, entity: f(c.a.entity) }, b: { ...c.b, entity: f(c.b.entity) } };
    case 'horizontal':
    case 'vertical':
    case 'radius':
      return { ...c, entity: f(c.entity) };
    case 'parallel':
    case 'perpendicular':
    case 'equal':
    case 'tangent':
    case 'concentric':
      return { ...c, a: f(c.a), b: f(c.b) };
    case 'distance':
      return { ...c, a: { ...c.a, entity: f(c.a.entity) }, b: { ...c.b, entity: f(c.b.entity) } };
    case 'fixed':
      return { ...c, point: { ...c.point, entity: f(c.point.entity) } };
    case 'symmetric':
      return {
        ...c,
        a: { ...c.a, entity: f(c.a.entity) },
        b: { ...c.b, entity: f(c.b.entity) },
        about: f(c.about),
      };
  }
}

/** Signed perpendicular distance of P from line A→B (positive = left). */
function bulgeOf(a: Vec2, b: Vec2, p: Vec2): number {
  const len = dist(a, b) || 1;
  return ((b[0] - a[0]) * (p[1] - a[1]) - (b[1] - a[1]) * (p[0] - a[0])) / len;
}

/** Entity → polyline for preview rendering. */
function entityPolyline(ent: SketchEntity): [Vec2[], boolean] {
  switch (ent.kind) {
    case 'line':
      return [[ent.a, ent.b], false];
    case 'rect':
      return [
        [ent.min, [ent.max[0], ent.min[1]], ent.max, [ent.min[0], ent.max[1]]],
        true,
      ];
    case 'circle': {
      const pts: Vec2[] = [];
      for (let i = 0; i < 48; i++) {
        const t = (i / 48) * Math.PI * 2;
        pts.push([ent.center[0] + ent.radius * Math.cos(t), ent.center[1] + ent.radius * Math.sin(t)]);
      }
      return [pts, true];
    }
    case 'ellipse': {
      const pts: Vec2[] = [];
      for (let i = 0; i < 48; i++) {
        const t = (i / 48) * Math.PI * 2;
        pts.push([ent.center[0] + ent.rx * Math.cos(t), ent.center[1] + ent.ry * Math.sin(t)]);
      }
      return [pts, true];
    }
    case 'arc': {
      let span = ent.endAngle - ent.startAngle;
      if (span <= 0) span += Math.PI * 2;
      const n = Math.max(8, Math.ceil(span * 12));
      const pts: Vec2[] = [];
      for (let i = 0; i <= n; i++) {
        const t = ent.startAngle + (span * i) / n;
        pts.push([ent.center[0] + ent.radius * Math.cos(t), ent.center[1] + ent.radius * Math.sin(t)]);
      }
      return [pts, false];
    }
    case 'spline':
      return [sampleSpline(ent.points, !!ent.closed), !!ent.closed];
    case 'poly':
      return [[...ent.points], true];
    case 'text': {
      // Hit-test/preview box approximation (real outlines render separately).
      const w = Math.max(ent.text.length * ent.height * 0.55, ent.height * 0.5);
      const [x, y] = ent.at;
      return [
        [[x, y], [x + w, y], [x + w, y + ent.height], [x, y + ent.height]],
        true,
      ];
    }
  }
}
